// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { Skeleton, Typography } from '@iheartjane/reefer';
import { spacing } from '@iheartjane/reefer-emotion';
import startCase from 'lodash-es/startCase';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { useManager, useStoreSettings } from '@jane/business-admin/data-access';
import { useModalActionsWithTracking } from '@jane/business-admin/hooks';
import { StoreDetailsContext } from '@jane/business-admin/providers';
import { BODY_LINE_HEIGHT, ModalNames } from '@jane/business-admin/util';

import { EditableCard } from '../../../../../EditableCard';
import { TableRowWithBorder } from '../../../../../TableWithBorderSeparator';
import { PaymentModal } from './PaymentModal';
import { SQUARE_OAUTH_REDIRECT_SEARCH_PARAM } from './SquareSettings';
import { shouldShowIntegration } from './shouldShowIntegration';

const PaymentTable = styled.table({
  borderCollapse: 'collapse',
  ...spacing({ mb: 32 }),
});

const TableHeaderItem = ({ label }: { label: string }) => (
  <td width="50%">
    <Typography variant="caps" mb={16} ml={16} color="grays-mid">
      {label}
    </Typography>
  </td>
);

const TableContentItem = ({ label }: { label?: string }) => (
  <td width="50%">
    <Typography m={16}>{label}</Typography>
  </td>
);

const LoadingTableColumns = () => (
  <tr>
    <td>
      <Skeleton animate height="fit-content" width="320px">
        <Skeleton.Bone mb={4} height={BODY_LINE_HEIGHT} m={16} />
      </Skeleton>
    </td>
    <td>
      <Skeleton animate height="fit-content" width="320px">
        <Skeleton.Bone mb={4} height={BODY_LINE_HEIGHT} m={16} />
      </Skeleton>
    </td>
  </tr>
);
export const PaymentCard = () => {
  const { modalOpen, openModal, closeModal } = useModalActionsWithTracking(
    ModalNames.Payments
  );

  const [searchParams] = useSearchParams();
  const [openAfterSquareOAuthRedirect, setOpenAfterSquareOAuthRedirect] =
    useState<boolean>(false);

  useEffect(() => {
    const squareOAuthRedirect = searchParams.get(
      SQUARE_OAUTH_REDIRECT_SEARCH_PARAM
    );

    if (squareOAuthRedirect !== null) {
      setOpenAfterSquareOAuthRedirect(true);
    }
  }, [searchParams]);

  const closePaymentCardModal = () => {
    setOpenAfterSquareOAuthRedirect(false);
    closeModal();
  };

  const { canEditStore, storeId } = useContext(StoreDetailsContext);
  const { data: storePayload, isFetching: storeSettingsLoading } =
    useStoreSettings(storeId);
  const { data: managerData } = useManager();
  const isInternal = managerData?.manager?.internal || false;
  const isOhio = storePayload?.store.state === 'Ohio';

  type storePaymentOptionsType = {
    autocapture: string | null;
    name: string;
  };

  const storePaymentOptions: storePaymentOptionsType[] = useMemo(() => {
    const paymentOptions = [];

    if (storePayload?.jane_pay_integration?.enabled)
      paymentOptions.push({
        name: 'Jane Pay',
        autocapture: storePayload?.jane_pay_integration?.autocapture_on_checkout
          ? 'On checkout'
          : storePayload?.jane_pay_integration?.autocapture_on_completion
            ? 'On completion'
            : null,
      });
    if (
      storePayload?.store.canpay_enabled &&
      shouldShowIntegration('canpay', { isInternal, storePayload })
    )
      paymentOptions.push({ name: 'CanPay', autocapture: null });
    if (
      storePayload?.moneris_integration?.enabled &&
      shouldShowIntegration('moneris', { isInternal, storePayload })
    )
      paymentOptions.push({
        name: 'Moneris',
        autocapture: storePayload?.moneris_integration?.autocapture_on_checkout
          ? 'On checkout'
          : null,
      });
    if (
      storePayload?.canpay_v2_integration?.enabled &&
      shouldShowIntegration('canpay_v2', { isInternal, storePayload })
    )
      paymentOptions.push({ name: 'CanPay V2', autocapture: null });
    if (
      storePayload?.canpay_v2remotepay_integration?.enabled &&
      shouldShowIntegration('canpay_v2remotepay', { isInternal, storePayload })
    )
      paymentOptions.push({ name: 'CanPay V2 RemotePay', autocapture: null });
    if (
      storePayload?.stronghold_integration?.enabled &&
      shouldShowIntegration('stronghold', { isInternal, storePayload })
    )
      paymentOptions.push({ name: 'Stronghold', autocapture: null });
    if (
      storePayload?.payfirma_integration?.enabled &&
      shouldShowIntegration('payfirma', { isInternal, storePayload })
    )
      paymentOptions.push({ name: 'Payfirma', autocapture: null });
    if (
      storePayload?.square_integration?.enabled &&
      shouldShowIntegration('square', { isInternal, storePayload })
    )
      paymentOptions.push({ name: 'Square', autocapture: null });

    if (!paymentOptions.length)
      paymentOptions.push({
        name: 'Payment providers have not been specified for this store.',
        autocapture: null,
      });

    return paymentOptions;
  }, [JSON.stringify(storePayload), isInternal]);

  const additionalPaymentOptions = useMemo(() => {
    const keyedOptions: {
      [k: string]: string[];
    } = {};

    storePayload?.payment_options
      .filter((paymentOptions) => paymentOptions.enabled)
      .forEach((paymentOption) => {
        const name = paymentOption.payment_option;
        const value = paymentOption.reservation_mode;

        if (Array.isArray(keyedOptions[name])) {
          keyedOptions[name].push(value);
        } else {
          keyedOptions[name] = [value];
        }
      });

    let options: { modes: string | null; name: string }[] = [];

    if (!Object.keys(keyedOptions).length) {
      options.push({
        name: 'No additional payment options have been specified for this store.',
        modes: null,
      });

      return options;
    }

    options = Object.entries(keyedOptions).map(([name, modes]) => ({
      name,
      modes: modes.join(', '),
    }));

    return options;
  }, [JSON.stringify(storePayload?.payment_options)]);

  return (
    <>
      <EditableCard
        title="Payments"
        onEdit={() => openModal()}
        isLoading={storeSettingsLoading}
        showTextButton={!storeSettingsLoading && !storePaymentOptions}
        textButtonLabel="Enable payment method"
        disabled={!canEditStore}
      >
        <PaymentTable aria-label="payment table">
          <thead>
            <tr>
              <TableHeaderItem label="Payment Providers" />
              {!storeSettingsLoading && !isOhio && (
                <TableHeaderItem label="Autocapture" />
              )}
            </tr>
          </thead>
          <tbody>
            {storeSettingsLoading ? (
              <LoadingTableColumns />
            ) : (
              storePaymentOptions.map((option, optionIndex) => (
                <TableRowWithBorder
                  key={optionIndex}
                  hasBorder={optionIndex < storePaymentOptions.length - 1}
                >
                  <>
                    <TableContentItem label={option.name} />
                    {!isOhio && (
                      <TableContentItem
                        label={option.autocapture || 'Disabled'}
                      />
                    )}
                  </>
                </TableRowWithBorder>
              ))
            )}
          </tbody>
        </PaymentTable>
        <PaymentTable aria-label="additional payment table">
          <thead>
            <tr>
              <TableHeaderItem label="Additional Payment Options" />
              {!storeSettingsLoading &&
              additionalPaymentOptions.length === 1 &&
              additionalPaymentOptions[0].modes === null ? null : (
                <TableHeaderItem label="Reservation Modes" />
              )}
            </tr>
          </thead>
          <tbody>
            {storeSettingsLoading ? (
              <LoadingTableColumns />
            ) : (
              additionalPaymentOptions.map((option, optionIndex) => (
                <TableRowWithBorder
                  key={optionIndex}
                  hasBorder={optionIndex < additionalPaymentOptions.length - 1}
                >
                  <>
                    <TableContentItem label={option.name} />
                    <TableContentItem
                      label={
                        option.modes
                          ?.split(',')
                          .map((mode) => startCase(mode))
                          .join('   + ') || ''
                      }
                    />
                  </>
                </TableRowWithBorder>
              ))
            )}
          </tbody>
        </PaymentTable>
      </EditableCard>

      {(modalOpen || openAfterSquareOAuthRedirect) && (
        <PaymentModal
          open
          onClose={() => closePaymentCardModal()}
          storeId={storeId}
          storePayload={storePayload}
        />
      )}
    </>
  );
};
